import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, MessageSquareWarning, AlertTriangle, Pencil, Filter } from "lucide-react";

const fmtMin = (m) => `${Math.floor((m || 0) / 60)}h ${Math.round((m || 0) % 60)}m`;
const fmtTime = (s) => (s ? new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminTimeClockPage() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("all");
  const [overOnly, setOverOnly] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const params = new URLSearchParams();
    if (userId !== "all") params.set("user_id", userId);
    if (overOnly) params.set("over_buffer_only", "true");
    const [r, u] = await Promise.all([api.get(`/timeclock/all?${params}`), api.get("/users")]);
    setEntries(r.data);
    setUsers(u.data);
  };
  useEffect(() => { load(); }, [userId, overOnly]);

  const userName = (id) => {
    const u = users.find(x => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : id;
  };

  const openEdit = (e) => {
    setEdit(e);
    setForm({
      clock_in: e.clock_in?.slice(0, 16),
      clock_out: e.clock_out?.slice(0, 16),
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/timeclock/${edit.id}`, {
        clock_in: form.clock_in ? new Date(form.clock_in).toISOString() : null,
        clock_out: form.clock_out ? new Date(form.clock_out).toISOString() : null,
      });
      toast.success("Korrigiert");
      setEdit(null);
      await load();
    } catch (err) { toast.error("Fehler"); }
  };

  const requestJustify = async (id) => {
    try { await api.post("/timeclock/request-justify", { entry_id: id }); toast.success("Anfrage gesendet"); await load(); }
    catch (err) { toast.error("Fehler"); }
  };

  const flagged = entries.filter(e => e.over_buffer && !e.justification).length;

  return (
    <div className="space-y-6" data-testid="admin-tc-root">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="overline">Verwaltung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Zeiterfassung (Alle Mitarbeiter)</h2>
          <p className="text-sm text-slate-500 mt-1">Einträge korrigieren, Überstunden begründen, Warnungen bearbeiten.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="w-56" data-testid="admin-tc-filter-user"><SelectValue placeholder="Alle Mitarbeiter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Mitarbeiter</SelectItem>
              {users.filter(u => u.role !== "kiosk").map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            data-testid="admin-tc-over-toggle"
            variant={overOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setOverOnly(!overOnly)}
          >
            <AlertTriangle className="w-4 h-4 mr-1" /> Nur Überstunden {flagged > 0 && `(${flagged})`}
          </Button>
        </div>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Mitarbeiter</TableHead>
                <TableHead>Beginn</TableHead>
                <TableHead>Ende</TableHead>
                <TableHead>Pause</TableHead>
                <TableHead>Netto</TableHead>
                <TableHead>Überstd.</TableHead>
                <TableHead>Begründung</TableHead>
                <TableHead>Quelle</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.slice(0, 200).map((e) => (
                <TableRow key={e.id} data-testid={`admin-tc-row-${e.id}`} className={e.over_buffer && !e.justification ? "bg-amber-50/50 dark:bg-amber-950/10" : ""}>
                  <TableCell className="font-medium">{e.date}</TableCell>
                  <TableCell>{userName(e.user_id)}</TableCell>
                  <TableCell className="text-xs">{fmtTime(e.clock_in)}</TableCell>
                  <TableCell className="text-xs">{fmtTime(e.clock_out)}</TableCell>
                  <TableCell>{Math.round(e.break_minutes || 0)}m</TableCell>
                  <TableCell className="font-semibold">{fmtMin(e.net_minutes)}</TableCell>
                  <TableCell>{(e.overtime_minutes || 0) > 0 ? <Badge variant="outline" className="text-amber-700 border-amber-300">{fmtMin(e.overtime_minutes)}</Badge> : "—"}</TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate">{e.justification || "—"}</TableCell>
                  <TableCell><Badge variant="secondary" className="text-[10px]">{e.source}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {e.over_buffer && !e.justification && !e.justification_requested && (
                        <Button size="sm" variant="outline" onClick={() => requestJustify(e.id)} title="Begründung anfordern">
                          <MessageSquareWarning className="w-3 h-3" />
                        </Button>
                      )}
                      <Button data-testid={`admin-tc-edit-${e.id}`} size="icon" variant="ghost" onClick={() => openEdit(e)}><Pencil className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-8">Keine Einträge</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Zeiterfassung korrigieren</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">{userName(edit.user_id)} · {edit.date}</div>
              <div><Label>Beginn</Label><Input data-testid="admin-tc-form-in" type="datetime-local" value={form.clock_in || ""} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} /></div>
              <div><Label>Ende</Label><Input data-testid="admin-tc-form-out" type="datetime-local" value={form.clock_out || ""} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Abbrechen</Button>
            <Button data-testid="admin-tc-save" onClick={saveEdit}><Save className="w-4 h-4 mr-2" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
